import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import getApiBaseUrl from '../utils/apiBaseUrl';

const API_BASE_URL = getApiBaseUrl();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Logout function
  const signOut = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  // Token validation function
  const verifyToken = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/verify`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // If the account is inactive, log out
      if (response.data.inactive) {
        signOut();
        return false;
      }
      
      return response.data.valid === true;
    } catch (error) {
      // If a 403 inactive error occurs, log out
      if (error.response?.status === 403 && error.response?.data?.inactive) {
        signOut();
      }
      return false;
    }
  }, [signOut]);

  // Check the saved token on load
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        // Validate the token on the server
        verifyToken(token).then((isValid) => {
          if (isValid) {
            setUser(userData);
          } else {
            // The token is invalid, clear it
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }).catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }).finally(() => {
          setLoading(false);
        });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  // Periodically check the trader status (every 30 seconds)
  useEffect(() => {
    if (!user) return;

    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(`${API_BASE_URL}/auth/status`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data.success) {
          const status = response.data.status?.toLowerCase();
          // If the status becomes inactive, automatically log out
          if (status === 'inactive') {
            signOut();
            // Show a message to the user
            alert('Your account has been deactivated by the administrator. You have been automatically logged out.');
            window.location.href = '/login';
          }
        }
      } catch (error) {
        // If a 403 inactive error occurs, log out
        if (error.response?.status === 403 && error.response?.data?.inactive) {
          signOut();
          alert('Your account has been deactivated by the administrator. You have been automatically logged out.');
          window.location.href = '/login';
        }
      }
    };

    // Check immediately on mount
    checkStatus();

    // Set up a periodic check every 30 seconds
    const interval = setInterval(checkStatus, 30000);

    return () => clearInterval(interval);
  }, [user, signOut]);

  const signIn = async (login, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        login,
        password
      });

      if (response.data.success && response.data.token) {
        const userData = {
          traderId: response.data.traderId,
          gatewayId: response.data.gatewayId || null,
          login: response.data.login,
          name: response.data.name,
          role: response.data.role || 'trader',
          isAdmin: response.data.isAdmin || false
        };
        
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        
        return { success: true };
      } else {
        return { success: false, error: response.data.error || 'Login error' };
      }
    } catch (error) {
      if (error.response) {
        return { 
          success: false, 
          error: error.response.data?.error || error.response.data?.message || 'Login error' 
        };
      }
      return { success: false, error: error.message || 'Server connection error' };
    }
  };

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
    getToken,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
